import React, { useState, useEffect } from "react";
import { 
  Table, Tag, Typography, Input, Select, Button, Row, Col, Card, 
  Spin, Empty, Space, message, DatePicker, Avatar, Dropdown, Menu,
  Tooltip, Badge
} from "antd";
import { 
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined
} from "@ant-design/icons";
import axiosInstance from "../../Config/axiosConfig";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    transactionId: "",
    customerName: "",
    status: null,
    dateRange: null,
  });

  // Fetch payments data
  const fetchPayments = async () => {
    try {
      setLoading(true);
      
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        message.error("Please login first");
        return;
      }

      const response = await axiosInstance.post(
        "Admin/get-payments",
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const data = response.data;

      if (data.code === 200) {
        // Format payments data
        const formattedPayments = data.data.payments?.map(payment => ({
          key: payment.id || payment.transactionId,
          id: payment.id || payment.transactionId,
          customer: {
            name: payment.customerName || payment.user?.name || "Unknown",
            email: payment.customerEmail || payment.user?.email,
          },
          date: formatDate(payment.createdAt || payment.timestamp),
          amount: payment.amount || 0,
          currency: payment.currency || "USD",
          status: payment.status?.toLowerCase() || "pending",
          paymentMethod: payment.paymentMethod?.toLowerCase() || "card",
          description: payment.description || "Payment",
        })) || [];

        setPayments(formattedPayments);
      } else {
        message.error(data.message || "Failed to load payments");
      }

    } catch (error) {
      console.error("Error fetching payments:", error);
      message.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Initial load
  useEffect(() => {
    fetchPayments();
  }, []);

  // Apply filters
  const handleApplyFilters = () => {
    fetchPayments();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      transactionId: "",
      customerName: "",
      status: null,
      dateRange: null,
    });
    fetchPayments();
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    if (filters.transactionId && !payment.id.includes(filters.transactionId)) {
      return false;
    }
    if (filters.customerName && !payment.customer.name.toLowerCase().includes(filters.customerName.toLowerCase())) {
      return false;
    }
    if (filters.status && payment.status !== filters.status) {
      return false;
    }
    return true;
  });

  // Get status config
  const getStatusConfig = (status) => {
    switch(status) {
      case "completed":
      case "success":
        return { color: "green", icon: <CheckCircleOutlined />, text: "Completed" };
      case "pending":
        return { color: "orange", icon: <ClockCircleOutlined />, text: "Pending" };
      case "failed":
      case "canceled":
        return { color: "red", icon: <CloseCircleOutlined />, text: "Failed" };
      default:
        return { color: "default", icon: <ClockCircleOutlined />, text: "Processing" };
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch(method) {
      case "card": return <CreditCardOutlined />;
      case "paypal": return <BankOutlined />;
      case "bank_transfer": return <BankOutlined />;
      case "wallet": return <WalletOutlined />;
      default: return <CreditCardOutlined />;
    }
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  // Table columns
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (id) => <Text style={{ fontFamily: 'monospace' }}>{id}</Text>
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 180,
      render: (customer) => (
        <div>
          <div style={{ fontWeight: 500 }}>{customer.name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{customer.email}</Text>
        </div>
      )
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      sorter: (a, b) => a.amount - b.amount,
      render: (amount, record) => (
        <Text strong style={{ color: amount > 0 ? '#52c41a' : '#f5222d' }}>
          {formatCurrency(amount, record.currency)}
        </Text>
      )
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 100,
      render: (method) => (
        <Tooltip title={method.toUpperCase()}>
          {getPaymentMethodIcon(method)}
        </Tooltip>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item icon={<EyeOutlined />}>
                View Details
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      )
    }
  ];

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Transactions</span>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchPayments} 
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Transaction ID"
            value={filters.transactionId}
            onChange={(e) => setFilters({...filters, transactionId: e.target.value})}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Customer Name"
            value={filters.customerName}
            onChange={(e) => setFilters({...filters, customerName: e.target.value})}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Status"
            style={{ width: '100%' }}
            value={filters.status}
            onChange={(value) => setFilters({...filters, status: value})}
            allowClear
          >
            <Option value="completed">Completed</Option>
            <Option value="pending">Pending</Option>
            <Option value="failed">Failed</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              icon={<FilterOutlined />}
              onClick={handleApplyFilters}
              type="primary"
              style={{ flex: 1 }}
            >
              Filter
            </Button>
            <Button 
              onClick={handleResetFilters}
              style={{ flex: 1 }}
            >
              Clear
            </Button>
          </div>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : filteredPayments.length === 0 ? (
        <Empty description="No payment transactions found" />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredPayments}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} transactions`
          }}
          scroll={{ x: 1000 }}
          size="middle"
        />
      )}
    </Card>
  );
};

export default Payments;